import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { InfrastructuresService } from '@core/api/generated';
import { ApiSysService } from '@core/api/api-sys.service';
import { AuthService } from '@core/auth/auth.service';
import { throwApiError } from '@core/error';
import { InfrastructureCatalogItem } from './models/infrastructure-catalog.model';

/**
 * Reference-data loader for infrastructures (guide §6): loaded on demand and
 * exposed as a read-only signal so any shared/feature component can reuse the
 * same lookups instead of each one making its own API calls.
 */
@Injectable({ providedIn: 'root' })
export class InfrastructureCatalogService {
    private readonly infrastructuresService = inject(InfrastructuresService);
    private readonly apiSys = inject(ApiSysService);
    private readonly auth = inject(AuthService);

    private readonly _infrastructures = signal<InfrastructureCatalogItem[]>([]);
    readonly infrastructures = this._infrastructures.asReadonly();

    async loadAll(translations?: any): Promise<InfrastructureCatalogItem[]> {
        const page = await firstValueFrom(
            this.infrastructuresService.listInfrastructuresApiV1InfrastructuresGet('list', 'all'),
        );
        const items: InfrastructureCatalogItem[] = (page.items ?? []).map((item: any) => ({
            id: item.id,
            str_id: item.str_code ?? '',
            str_name: item.name,
            str_denom: item.name,
            str_iata: item.iata ?? undefined,
            str_icao: item.icao ?? undefined,
            str_local: item.local ?? undefined,
            category: item.categories ?? [],
            hasAccess: true,
            isPetitioned:
                item.per_infrastructure_status != null &&
                item.per_infrastructure_status !== 'ACT',
        }));

        this._infrastructures.set(items);
        return this._infrastructures();
    }

    async loadByUserId(id: number, translations?: any): Promise<InfrastructureCatalogItem[]> {
        const { data, error } = await this.apiSys.getInfrastructuresByUserId(this.auth.getToken(), id);
        if (error) throwApiError(error, translations, 'FETCH_INFRASTRUCTURE_ERROR');

        this._infrastructures.set(data ?? []);
        return this._infrastructures();
    }

    async loadByOrgId(id: number, translations?: any): Promise<InfrastructureCatalogItem[]> {
        const { data, error } = await this.apiSys.getInfrastructuresByOrgId(this.auth.getToken(), id);
        if (error) throwApiError(error, translations, 'FETCH_INFRASTRUCTURE_ERROR');

        this._infrastructures.set(data ?? []);
        return this._infrastructures();
    }

    /** Infrastructures already assigned to an organization (distinct from `loadByOrgId`'s org-visibility list). */
    async loadAssignedToOrg(id: number, translations?: any): Promise<InfrastructureCatalogItem[]> {
        const { data, error } = await this.apiSys.getInfsOfOrg(this.auth.getToken(), id);
        if (error) throwApiError(error, translations, 'FETCH_INFRASTRUCTURE_ERROR');

        this._infrastructures.set(data ?? []);
        return this._infrastructures();
    }

    async filterByName(name: string, translations?: any): Promise<InfrastructureCatalogItem[]> {
        const { data, error } = await this.apiSys.getInfrastructureFilterResults(this.auth.getToken(), name);
        if (error) throwApiError(error, translations, 'FETCH_INFRASTRUCTURE_ERROR');

        return data ?? [];
    }

    async getByCode(code: string, translations?: any): Promise<InfrastructureCatalogItem | undefined> {
        const { data, error } = await this.apiSys.getInfrastructureByCode(this.auth.getToken(), code);
        if (error) throwApiError(error, translations, 'FETCH_INFRASTRUCTURE_ERROR');

        return data;
    }
}
